/**
 * ConfirmOnEntryDialog Component
 *
 * Prompts the user to fill a required field before a card can enter a column.
 * Used when a column has a confirmOnEntry rule configured.
 *
 * When fieldConfig.requireReflection is true, also renders three reflection
 * buttons below the numeric input:
 *   - Understood (green)
 *   - Still thinking (amber)
 *   - I need help (red)
 * Both field value and reflection must be provided before the form submits.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  ConfirmOnEntryConfig,
  CONFIRMABLE_FIELD_OPTIONS,
  ConfirmableField,
} from '@/types/board-config.types';
import type { BoardTask } from '@/types/models/BoardTask';
import { BoardTaskReflection } from '@/types/enums/BoardTaskReflection';
import { useThemeColors } from '@/styles';

interface ConfirmOnEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  card: BoardTask;
  fieldConfig: ConfirmOnEntryConfig;
  onConfirm: (value: number, reflection?: BoardTaskReflection) => void;
  isSubmitting: boolean;
}

interface ReflectionOption {
  value: BoardTaskReflection;
  label: string;
}

const REFLECTION_OPTIONS: ReflectionOption[] = [
  { value: BoardTaskReflection.UNDERSTOOD, label: 'Understood' },
  { value: BoardTaskReflection.STILL_THINKING, label: 'Still thinking' },
  { value: BoardTaskReflection.NEED_HELP, label: 'I need help' },
];

function getFieldOption(field: ConfirmableField) {
  return CONFIRMABLE_FIELD_OPTIONS.find(o => o.value === field);
}

export function ConfirmOnEntryDialog({
  open,
  onOpenChange,
  card,
  fieldConfig,
  onConfirm,
  isSubmitting,
}: ConfirmOnEntryDialogProps) {
  const colors = useThemeColors();
  const getReflectionStyle = (
    value: BoardTaskReflection,
    selected: boolean
  ) => {
    const key =
      value === BoardTaskReflection.UNDERSTOOD
        ? 'understood'
        : value === BoardTaskReflection.STILL_THINKING
          ? 'stillThinking'
          : 'needHelp';
    const { bg, text } = colors.reflection[key];
    return {
      backgroundColor: selected ? text : bg,
      color: selected ? colors.text.inverse : text,
      border: `1.5px solid ${text}`,
    };
  };
  const option = getFieldOption(fieldConfig.field);
  const existingValue =
    (card[fieldConfig.field as keyof BoardTask] as number | undefined) ?? null;
  const [value, setValue] = useState<string>(
    existingValue != null && existingValue > 0 ? String(existingValue) : ''
  );
  const [reflection, setReflection] = useState<BoardTaskReflection | null>(
    null
  );

  // Reset state when dialog opens with a new card
  useEffect(() => {
    if (open) {
      const existing =
        (card[fieldConfig.field as keyof BoardTask] as number | undefined) ??
        null;
      setValue(existing != null && existing > 0 ? String(existing) : '');
      setReflection(card.reflection ?? null);
    }
  }, [open, card, fieldConfig.field]);

  const numericValue = parseInt(value, 10);
  const isFieldValid = !isNaN(numericValue) && numericValue > 0;
  const isReflectionValid =
    !fieldConfig.requireReflection || reflection !== null;
  const isValid = isFieldValid && isReflectionValid;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      onConfirm(numericValue, reflection ?? undefined);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{option?.label ?? 'Required Field'}</DialogTitle>
            <DialogDescription>
              {option?.prompt ??
                'Please fill in the required field before moving this card.'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 flex flex-col gap-4">
            <input
              type="number"
              min={1}
              step={1}
              value={value}
              onChange={e => setValue(e.target.value)}
              autoFocus
              className="w-full px-3 py-2 rounded-md border text-sm"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border.default,
                color: colors.text.primary,
              }}
              placeholder="Enter a number"
            />

            {fieldConfig.requireReflection && (
              <div className="flex flex-col gap-2">
                <p
                  className="text-sm font-medium"
                  style={{ color: colors.text.primary }}
                >
                  How did this task go?
                </p>
                <div className="flex gap-2">
                  {REFLECTION_OPTIONS.map(opt => {
                    const selected = reflection === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setReflection(opt.value)}
                        className="flex-1 px-3 py-2 rounded-md text-sm font-medium transition-opacity"
                        style={{
                          ...getReflectionStyle(opt.value, selected),
                          opacity: isSubmitting ? 0.6 : 1,
                        }}
                        disabled={isSubmitting}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="sm:justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Confirm & Move'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
